import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useCallback } from 'react';

import { JobSurge, paySurgeClient } from '../api';

export const jobSurgeQueryKey = (jobId: number | string) =>
  ['pay_surge', 'job', String(jobId)] as const;

/**
 * One job's surge pay (GRAVY-10443), shared by the job banner, Manage surge
 * pay and the override dialog.
 *
 * Every write answers with the job's new schedule, so `apply` writes that
 * answer straight into the cache instead of refetching — the banner repaints
 * from what the server decided. It also invalidates Dashboard V2, whose card
 * badge and panel carry the same amount.
 */
const useJobSurge = (
  jobId: number | string | null | undefined,
  enabled = true,
  // A screen that shows the surge beside Dashboard V2 data passes that
  // screen's refresh, so the two never disagree (review D7-04).
  refresh: Pick<
    UseQueryOptions<JobSurge>,
    'staleTime' | 'refetchInterval' | 'refetchOnWindowFocus'
  > = {},
) => {
  const queryClient = useQueryClient();

  const query = useQuery<JobSurge>({
    queryKey: jobSurgeQueryKey(jobId ?? 0),
    queryFn: () => paySurgeClient.getJobSurge(jobId as number),
    enabled: enabled && jobId != null,
    staleTime: 30_000,
    refetchInterval: 60_000,
    ...refresh,
  });

  const apply = useCallback(
    (surge: JobSurge) => {
      queryClient.setQueryData(jobSurgeQueryKey(surge.job_id), surge);
      queryClient.invalidateQueries({ queryKey: ['dashboard_v2'] });
    },
    [queryClient],
  );

  return { ...query, apply };
};

export default useJobSurge;
